// Copyright (C) 2020-2022 Intel Corporation
// Copyright (C) 2022-2023 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import './styles.scss';
import React, { useEffect, useState } from 'react';
import { Row, Col } from 'antd/lib/grid';
import Select from 'antd/lib/select';
import Text from 'antd/lib/typography/Text';
import InputNumber from 'antd/lib/input-number';
import Button from 'antd/lib/button';
import Switch from 'antd/lib/switch';
import Modal from 'antd/lib/modal';
import notification from 'antd/lib/notification';

import CVATTooltip from 'components/common/cvat-tooltip';
import { clamp } from 'utils/math';
import {
    MLModel, ModelKind, ModelReturnType, DimensionType, Label,
} from 'cvat-core-wrapper';

import { ServerMapping } from 'reducers';
import { getCVATStore } from 'cvat-store';
import LabelsMapperComponent, { LabelInterface, FullMapping } from './labels-mapper';

interface Props {
    withCleanup: boolean;
    models: MLModel[];
    labels: Label[];
    dimension: DimensionType;
    detectShortcut: string;
    runInference(model: MLModel, body: object): void;
}

export interface DetectorRequestBody {
    mapping: ServerMapping;
    cleanup: boolean;
    convMaskToPoly: boolean;
}

function convertMappingToServer(mapping: FullMapping): ServerMapping {
    return mapping.reduce<ServerMapping>((acc, [modelLabel, taskLabel, attributesMapping, subMapping]) => (
        {
            ...acc,
            [modelLabel.name]: {
                name: taskLabel.name,
                attributes: attributesMapping.reduce<Record<string, string>>((attrAcc, val) => {
                    if (val[0]?.name && val[1]?.name) {
                        attrAcc[val[0].name] = val[1].name;
                    }
                    return attrAcc;
                }, {}),
                ...(subMapping.length ? { sublabels: convertMappingToServer(subMapping) } : {}),
            },
        }
    ), {});
}

function DetectorRunner(props: Props): JSX.Element {
    const {
        models, withCleanup, labels, dimension, detectShortcut, runInference,
    } = props;

    const [modelID, setModelID] = useState<string | null>(null);
    const [threshold, setThreshold] = useState<number>(0.5);
    const [distance, setDistance] = useState<number>(50);
    const [cleanup, setCleanup] = useState<boolean>(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [mapping, setMapping] = useState<FullMapping>([]);
    const [convertMasksToPolygons, setConvertMasksToPolygons] = useState<boolean>(false);
    const [modelLabels, setModelLabels] = useState<LabelInterface[]>([]);
    const [taskLabels, setTaskLabels] = useState<LabelInterface[]>([]);

    const model = models.find((_model): boolean => _model.id === modelID);
    const isDetector = model?.kind === ModelKind.DETECTOR;
    const isReId = model?.kind === ModelKind.REID;
    const isClassifier = model?.kind === ModelKind.CLASSIFIER;
    const convertMasks2PolygonVisible = isDetector &&
        (!model.returnType || model.returnType === ModelReturnType.MASK);
    const labelsMappingVisible = isDetector || isClassifier;

    const buttonEnabled =
        model && (model.kind === ModelKind.REID ||
            (model.kind === ModelKind.DETECTOR && mapping.length) ||
            (model.kind === ModelKind.CLASSIFIER && mapping.length));

    useEffect(() => {
        const converted = labels.map((label) => ({
            name: label.name,
            type: label.type,
            color: label.color,
            attributes: label.attributes.map((attr) => ({
                name: attr.name,
                input_type: attr.inputType,
                values: [...attr.values],
            })),
            sublabels: (label.structure?.sublabels || []).map((sublabel) => ({
                name: sublabel.name,
                type: sublabel.type,
                color: sublabel.color,
                attributes: sublabel.attributes.map((attr) => ({
                    name: attr.name,
                    input_type: attr.inputType,
                    values: [...attr.values],
                })),
            })),
        }));

        setTaskLabels(converted);
        if (model) {
            setModelLabels(model.labels);
            if (!model.labels.length && model.kind !== ModelKind.REID) {
                notification.warning({ message: '当前模型没有指定的标签' });
            }
        } else {
            setModelLabels([]);
        }
    }, [labels, model]);

    return (
        <div className='cvat-run-model-content'>
            <Row align='middle'>
                <Col span={4}>模型：</Col>
                <Col span={20}>
                    <Select
                        placeholder={dimension === DimensionType.DIMENSION_2D ? '选择一个模型' : '3D 标注当前无模型可选'}
                        disabled={dimension !== DimensionType.DIMENSION_2D}
                        style={{ width: '100%' }}
                        onChange={(_modelID: string): void => {
                            setModelID(_modelID);

                            const selectedDetectorModel = models.filter((_model) => _model.id === _modelID)[0];

                            const { job } = getCVATStore().getState().annotation;
                            job.selectedDetectorModel = selectedDetectorModel;
                        }}
                    >
                        {models.map(
                            (_model: MLModel): JSX.Element => (
                                <Select.Option value={_model.id} key={_model.id}>
                                    {_model.name}
                                </Select.Option>
                            ),
                        )}
                    </Select>
                </Col>
            </Row>
            {labelsMappingVisible && (
                <>
                    <Row justify='start' align='middle'>
                        <LabelsMapperComponent
                            key={modelID} // rerender when model switched
                            onUpdateMapping={
                                (_mapping: FullMapping) => {
                                    setMapping(_mapping);

                                    const { job } = getCVATStore().getState().annotation;
                                    const serverMapping = convertMappingToServer(_mapping);
                                    job.detectorServerMapping = serverMapping;
                                }
                            }
                            modelLabels={modelLabels}
                            taskLabels={taskLabels}
                        />
                    </Row>
                </>
            )}
            {convertMasks2PolygonVisible && (
                <div className='detector-runner-convert-masks-to-polygons-wrapper'>
                    <Switch
                        checked={convertMasksToPolygons}
                        onChange={(checked: boolean) => {
                            setConvertMasksToPolygons(checked);

                            const { job } = getCVATStore().getState().annotation;
                            job.detectorConvMask2Poly = checked;
                        }}
                    />
                    <Text>将遮盖转换为多边形</Text>
                </div>
            )}
            {isDetector && withCleanup && (
                <div className='detector-runner-clean-previous-annotations-wrapper'>
                    <Switch
                        checked={cleanup}
                        onChange={(checked: boolean): void => {
                            if (checked) {
                                setIsModalVisible(true);
                            } else {
                                setCleanup(false);
                            }
                        }}
                    />
                    <Modal
                        title='是否要清除已有的标注？'
                        visible={isModalVisible}
                        onOk={() => {
                            setCleanup(true);
                            setIsModalVisible(false);
                        }}
                        onCancel={() => {
                            setIsModalVisible(false);
                        }}
                        okText='是'
                        cancelText='否'
                    >
                        <p>该操作会清除当前任务中的所有标注，并且无法撤销！！！</p>
                    </Modal>
                    <Text>清除之前的标注</Text>
                </div>
            )}
            {isReId ? (
                <div>
                    <Row align='middle' justify='start'>
                        <Col>
                            <Text>阈值</Text>
                        </Col>
                        <Col offset={1}>
                            <CVATTooltip title='进行合并的最小相似度'>
                                <InputNumber
                                    min={0.01}
                                    step={0.01}
                                    max={1}
                                    value={threshold}
                                    onChange={(value: number | undefined | string | null) => {
                                        if (typeof value !== 'undefined' && value !== null) {
                                            setThreshold(clamp(+value, 0.01, 1));
                                        }
                                    }}
                                />
                            </CVATTooltip>
                        </Col>
                    </Row>
                    <Row align='middle' justify='start'>
                        <Col>
                            <Text>最大距离</Text>
                        </Col>
                        <Col offset={1}>
                            <CVATTooltip title='能够被合并的形状之间的最大距离'>
                                <InputNumber
                                    placeholder='Threshold'
                                    min={1}
                                    value={distance}
                                    onChange={(value: number | undefined | string | null) => {
                                        if (typeof value !== 'undefined' && value !== null) {
                                            setDistance(+value);
                                        }
                                    }}
                                />
                            </CVATTooltip>
                        </Col>
                    </Row>
                </div>
            ) : null}
            <Row align='middle' justify='end'>
                <Col>
                    { detectShortcut ? (
                        <CVATTooltip overlay={`按 "${detectShortcut}" 完成`}>
                            <Button
                                className='cvat-inference-run-button'
                                disabled={!buttonEnabled}
                                type='primary'
                                onClick={() => {
                                    if (!model) return;
                                    const serverMapping = convertMappingToServer(mapping);
                                    if (model.kind === ModelKind.DETECTOR) {
                                        runInference(model, {
                                            mapping: serverMapping,
                                            cleanup,
                                            convMaskToPoly: convertMasksToPolygons,
                                        });
                                    } else if (model.kind === ModelKind.REID) {
                                        runInference(model, { threshold, max_distance: distance });
                                    } else if (model.kind === ModelKind.CLASSIFIER) {
                                        runInference(model, { cleanup, mapping: serverMapping });
                                    }
                                }}
                            >
                                检测
                            </Button>
                        </CVATTooltip>
                    ) : (
                        <Button
                            className='cvat-inference-run-button'
                            disabled={!buttonEnabled}
                            type='primary'
                            onClick={() => {
                                if (!model) return;
                                const serverMapping = convertMappingToServer(mapping);
                                if (model.kind === ModelKind.DETECTOR) {
                                    runInference(model, {
                                        mapping: serverMapping,
                                        cleanup,
                                        convMaskToPoly: convertMasksToPolygons,
                                    });
                                } else if (model.kind === ModelKind.REID) {
                                    runInference(model, { threshold, max_distance: distance });
                                } else if (model.kind === ModelKind.CLASSIFIER) {
                                    runInference(model, { cleanup, mapping: serverMapping });
                                }
                            }}
                        >
                            检测
                        </Button>
                    )}
                </Col>
            </Row>
        </div>
    );
}

export default React.memo(DetectorRunner);
